import { ArrowLeft, KeyRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useEmailToken } from "../hooks/useEmailToken.js";

import { Action, ErrorLine, LinkAction, Screen } from "../components/Screen.js";
import { Field } from "../components/Field.js";
import { consumeSignInLink, requestSignInLink } from "../services/authService.js";

/**
 * The other end of the sign-in link — `/signin/<token>`.
 *
 * # Why a page and not a `GET` route on the server
 *
 * The same reason `/verify` is one, and it matters more here. A `GET` that
 * signed somebody in would be triggered by any mail client or corporate scanner
 * that prefetches links, and this token is **single use** — so the scanner
 * spends it, and the person it was sent to has never once seen a link that
 * worked. The link opens a page; the page posts.
 *
 * The post is guarded by a ref, because React's StrictMode runs an effect twice
 * in development and the second run would spend a token the first already used
 * — showing "this link has expired" to somebody who clicked once.
 *
 * # It navigates rather than reporting success
 *
 * There is nothing to say. The session cookie is set by the time the promise
 * resolves, so this lands on the front door signed in, which is the thing they
 * were trying to do. A "you are signed in!" screen with a button on it is a
 * second click for no information.
 */
export function SignInLinkPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useEmailToken();
  const [state, setState] = useState<"working" | "failed">("working");
  const spent = useRef(false);

  useEffect(() => {
    if (!token || spent.current) {
      return;
    }
    spent.current = true;

    void (async () => {
      try {
        await consumeSignInLink(token);
        // `replace`, so Back does not return to a URL holding a spent token —
        // which would fail and look like the account had signed itself out.
        void navigate("/", { replace: true });
      } catch {
        setState("failed");
      }
    })();
  }, [token, navigate]);

  if (token && state === "working") {
    return <Screen title={t("signIn.link.working")}>{null}</Screen>;
  }

  // Two arrivals, one form.
  //
  // **Why the copy splits and the failure reason does not.** The server will not
  // say whether a link was expired, spent or invented — one `bad_token` for all
  // four — so this screen must never guess between *those*. But "you followed a
  // dead link" and "you came here to ask for one" are distinguishable without
  // asking the server anything: the difference is whether there was a token in
  // the URL at all. Telling somebody their link expired when they never had one
  // is the screen apologising for a thing that did not happen.
  //
  // Phase 20a made the second arrival real: the front door sends anybody who
  // would rather not type a password straight here.
  //
  // ⚠️ `useEmailToken` returns the **empty string** when there is no token in
  // the path, never `undefined` — so this asks whether it is empty. Comparing
  // against `undefined` is always true and quietly makes every arrival the
  // dead-link one; a test caught exactly that.
  const dead = token !== "";

  return (
    <Screen
      title={dead ? t("signIn.link.deadTitle") : t("signIn.link.title")}
      lead={dead ? t("signIn.link.deadLead") : t("signIn.link.lead")}
      footer={
        <div className="grid w-full grid-cols-2 gap-2">
          <LinkAction to="/login" icon={KeyRound}>
            {t("signIn.link.usePassword")}
          </LinkAction>
          <LinkAction to="/" icon={ArrowLeft}>
            {t("common.back")}
          </LinkAction>
        </div>
      }
    >
      <AnotherLinkBox />
    </Screen>
  );
}

/** The one box this screen is for: an address to send a link to. */
function AnotherLinkBox() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (sent) {
    return <p className="text-sm text-white/60">{t("signIn.link.sent")}</p>;
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        void requestSignInLink(email.trim()).then(
          () => {
            setSent(true);
          },
          () => {
            // Only a transport failure can land here — the route answers the
            // same way whether or not the address has an account, so there is
            // no "unknown address" case to report.
            setError(t("signIn.link.failed"));
            setBusy(false);
          },
        );
      }}
      className="flex flex-col gap-3"
    >
      <Field
        id="signin-email"
        label={t("signIn.link.emailLabel")}
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
        autoCapitalize="none"
        required
      />
      {error && <ErrorLine>{error}</ErrorLine>}
      <Action disabled={busy || !email.trim()}>{t("signIn.link.send")}</Action>
    </form>
  );
}
